import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENUpRightFromSquare from "@tCentro/Icons/CENUpRightFromSquare";
import CENWrench from "@tCentro/Icons/CENWrench";
import React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    subheading = "subheading",
    configureProduct = "configureProduct",
    checkboxFieldValue = "checkboxFieldValue",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.configureProduct]: string;
        [fields.checkboxFieldValue]: boolean;
    };
}

type Props = OwnProps & HasProduct;

export interface PDPRegisterBlockStyles {
    mainContainer?: InjectableCss;
    wrenchIcon?: IconPresentationProps;
    content?: InjectableCss;
    heading?: InjectableCss;
    subHeading?: InjectableCss;
    configureProduct?: InjectableCss;
    arrowIcon?: IconPresentationProps;
    linkPart?: InjectableCss;
    secondPart?: InjectableCss;
}

export const pdpRegisterBlockStyles: PDPRegisterBlockStyles = {
    mainContainer: {
        css: css`
            padding: 16px;
            gap: 8px;
            display: flex;
            background-color: #e0dfe0;
            box-sizing: border-box;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 16px;
                    `,
                    css`
                        margin-top: 16px;
                    `,
                    css`
                        margin-top: 16px;
                    `,
                    css`
                        margin-top: 16px;
                    `,
                    css`
                        margin-top: 16px;
                    `,
                ])}
        `,
    },
    wrenchIcon: {
        size: 48,
    },
    content: {
        css: css`
            gap: 8px;
            display: flex;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css``,
                    css`
                        width: 85%;
                    `,
                    css``,
                ])}
        `,
    },
    heading: {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 14px;
            line-height: 16px;
            color: #26252c;
        `,
    },
    subHeading: {
        css: css`
            font-family: Poppins;
            font-weight: 400;
            font-size: 12px;
            line-height: 16px;
            color: ${getProp("theme.typography.p.color")};
            max-width: 100%;
            text-wrap: wrap;
        `,
    },

    configureProduct: {
        css: css`
            font-weight: 600;
            font-size: 12px;
            line-height: 16px;
            color: ${getColor("text.link")};
            font-family: Poppins;
            white-space: nowrap;
            max-width: 100%;
            span {
                display: flex;
                align-items: center;
            }
        `,
    },
    arrowIcon: {
        size: 16,
        css: css`
            margin-left: 2px;
            svg {
                > path {
                    fill: ${getColor("text.link")};
                }
            }
        `,
    },
    linkPart: {
        css: css`
            display: flex;
        `,
    },
    secondPart: {
        css: css`
            display: flex;
            gap: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        width: 100%;
                    `,
                    css`
                        flex-direction: column;
                        width: 100%;
                    `,
                    css`
                        flex-direction: column;
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                ])}
        `,
    },
};

const styles = pdpRegisterBlockStyles;

const CENProductConfigureBrandLink: FC<Props> = ({ fields, product }) => {
    if (!product.brand?.properties?.configuratorUrl) {
        return null;
    }
    return (
        <>
            <StyledWrapper {...styles.mainContainer}>
                <StyledWrapper>
                    <Icon data-test-selector="wrenchIcon" src={CENWrench} {...styles.wrenchIcon} />
                </StyledWrapper>

                <StyledWrapper {...styles.content}>
                    {fields.heading && <StyledWrapper {...styles.heading}>{fields.heading}</StyledWrapper>}
                    <StyledWrapper {...styles.secondPart}>
                        {fields.subheading && (
                            <Typography {...styles.subHeading}>
                                {fields.subheading} {`${product?.brand.name} products`}
                            </Typography>
                        )}
                        <StyledWrapper {...styles.linkPart}>
                            <Link
                                href={product?.brand?.properties?.configuratorUrl}
                                {...styles.configureProduct}
                                target={fields.checkboxFieldValue ? "_blank" : "_self"}
                            >
                                {`${fields.configureProduct}`}
                                {fields.configureProduct && <Icon src={CENUpRightFromSquare} {...styles.arrowIcon} />}
                            </Link>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(CENProductConfigureBrandLink),
    definition: {
        group: "Common",
        displayName: "CENProductConfigureBrandLink",
        fieldDefinitions: [
            {
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.subheading,
                editorTemplate: "MultilineTextField",
                displayName: "subHeading",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.configureProduct,
                editorTemplate: "TextField",
                displayName: "Link Lable",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.checkboxFieldValue,
                displayName: "Open in a new tab",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
